import UserAPI from '../../api/UserAPI';
import { IMango } from '../../types/feedTypes';
import {
  IUser,
  IUserDetails,
  OnboardingStatus,
  UAMPermissions,
} from '../../types/userTypes';
import {
  setIsCommunityEnabled,
  setIsGamificationEnabled,
  setUserTier,
} from '../appReducer';
import {
  setSubscribedMangoes as setAllSubscribedMangoes,
} from '../mangoReducer';
import { AppThunk } from '../store';
import {
  checkForPermissions,
  setSubscribedMangoes,
  setUserDetails,
} from '../userReducer';

const DEFAULT_PROFILE_PIC =
  'https://tagmango.com/staticassets/avatar-placeholder.png-1612857612139.png';

/**
 * Maps API user-details result to the store's IUser shape.
 * Use this when calling setUserDetails after getLatestUserDetails().
 */
const mapApiUserDetailsToStore = (apiResult: IUserDetails): Partial<IUser> => ({
  name: apiResult.name,
  email: apiResult.email ?? null,
  phone: apiResult.phone ?? null,
  profilePic: apiResult.profilePicUrl || DEFAULT_PROFILE_PIC,
  type: apiResult.onboarding as OnboardingStatus,
  id: apiResult._id ?? null,
  userTier: apiResult.userTier,
  accountSettingTierSubscriptionExpiry:
    apiResult.accountSettingTierSubscriptionExpiry,
  accountSettingTierRazorpaySubscriptionIsAutopayStatus:
    apiResult.accountSettingTierRazorpaySubscriptionIsAutopayStatus,
  scheduledPlanActivateAt: apiResult.scheduledPlanActivateAt,
  scheduledPlanTier: apiResult.scheduledPlanTier,
  userSlug: apiResult.userSlug ?? null,
  showInbox: apiResult.showInbox ?? false,
  approveStatus: apiResult.creatorAccessRequested ?? null,
  shortUrl: apiResult.shortUrl ?? '',
  disabledChat: apiResult.disabledChat,
  gstNumber: apiResult.gstNumber,
  currency: apiResult.currency ?? 'INR',
  tmCommission: apiResult?.tmCommission ?? 0,
  country: apiResult.country,
  isEmailVerified: apiResult.isEmailVerified,
  requiresPostMigrationVerification:
    apiResult.requiresPostMigrationVerification,
  isBlockedFromCommunityEngagement: apiResult.isBlockedFromCommunityEngagement,
  score: apiResult.score,
  dialCode: apiResult.dialCode,
  userAccessPermissions: apiResult.userAccessPermissions,
  featuresConfig: apiResult?.featuresConfig,

  tqConnectScore: apiResult?.tqConnectScore,
  tqConnectProfileCompletionResponses:
    apiResult?.tqConnectProfileCompletionResponses,
  tqConnectBadge: apiResult?.tqConnectBadge,
  tqScoreLastUpdatedAt: apiResult?.tqScoreLastUpdatedAt,
  tqReviewReceived: apiResult?.tqReviewReceived,
});

/**
 * Fetches latest user details from API and dispatches setUserDetails with the mapped data.
 * Returns the API result on success, null otherwise. Use in App and VerifyOTPBox.
 */
const fetchAndSetUserDetails =
  (): AppThunk<Promise<IUserDetails | null>> => async (dispatch) => {
    const response = await UserAPI.getLatestUserDetails();
    const result = response?.data?.result;
    if (response?.status === 200 && result) {
      dispatch(setUserDetails(mapApiUserDetailsToStore(result)));
      return result;
    }
    return null;
  };

const updateUserTierDetails = (): AppThunk => async (dispatch, getState) => {
  const { hostMetadata } = getState().app;

  const userTier = hostMetadata.creator.userTier;

  dispatch(setUserTier(userTier));
};

const updateSubscribedMangoes =
  (mangoes: IMango[]): AppThunk =>
  async (dispatch, getState) => {
    const {
      hostMetadata: { gamifiedMangoes, creator, communityEnabledMangoes },
    } = getState().app;
    const userDetails = getState().user;

    if (gamifiedMangoes && gamifiedMangoes.length > 0) {
      if (
        userDetails.id === creator?._id ||
        (userDetails.type === 'creator_restricted' &&
          checkForPermissions(
            [UAMPermissions.DASHBOARD_GAMIFICATION_ACCESS],
            userDetails.userAccessPermissions,
          ))
      ) {
        dispatch(setIsGamificationEnabled(true));
      } else if (mangoes && mangoes.length > 0) {
        const result = mangoes.filter((mango) =>
          gamifiedMangoes.includes(mango._id),
        );
        dispatch(setIsGamificationEnabled(result.length > 0));
      }
    }

    if (communityEnabledMangoes && communityEnabledMangoes.length > 0) {
      if (
        userDetails.id === creator?._id ||
        userDetails.type === 'creator_restricted'
      ) {
        dispatch(setIsCommunityEnabled(true));
      } else if (mangoes && mangoes.length > 0) {
        const result = mangoes.filter((mango) =>
          communityEnabledMangoes.includes(mango._id),
        );

        dispatch(setIsCommunityEnabled(result.length > 0));
      }
    }

    dispatch(setSubscribedMangoes(mangoes));
    dispatch(setAllSubscribedMangoes(mangoes));
  };

export {
  fetchAndSetUserDetails,
  mapApiUserDetailsToStore,
  updateSubscribedMangoes,
  updateUserTierDetails,
};
